/**
 * The characters in the chat and in Nearby: a hand-drawn pack of odd little faces on a 72 grid (since 0.22.7). Each
 * is a soft tombstone body in Charcoal or Passwork Blue — its own build, a little wider or narrower, its crown a touch
 * off-centre, its sides gently bowed — leaning a little but centred in the white circle, peeking up from its lower
 * edge, with a big white face: googly, cyclops, three-eyed, dizzy, a vampire, a pirate and more.
 */

type Draw = (body: string) => string;
interface Face {
  readonly name: string;
  readonly draw: Draw;
}
interface Build {
  readonly w: number;
  readonly top: number;
  readonly dx: number;
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly tilt: number;
}
interface EyeShape {
  readonly rx?: number;
  readonly ry?: number;
  readonly r?: number;
  readonly dx?: number;
  readonly dy?: number;
}

/** The two body colours: you are always the one, the other side the other. */
export const BODIES = { charcoal: '#1e2339', blue: '#3b82f6' } as const;
export type Tone = keyof typeof BODIES;

const W = '#ffffff';
const S = (w = 3.4): string => `fill="none" stroke="${W}" stroke-width="${String(w)}" stroke-linecap="round" stroke-linejoin="round"`;

/** A white eye with a pupil in the body colour, looking (dx, dy). */
const eye = (x: number, y: number, b: string, { rx = 5.4, ry = 6.4, r = 3, dx = 0, dy = 1 }: EyeShape = {}): string =>
  `<ellipse cx="${String(x)}" cy="${String(y)}" rx="${String(rx)}" ry="${String(ry)}" fill="${W}"/><circle cx="${String(x + dx)}" cy="${String(y + dy)}" r="${String(r)}" fill="${b}"/>`;
const spiral = (x: number, y: number): string => `<path d="M${String(x)} ${String(y)}m0 0.6a1.2 1.2 0 1 1 1.4-1.6a2.8 2.8 0 1 1-3.4 3.4a4.4 4.4 0 1 1 5.6-5" ${S(2)}/>`;
const cross = (x: number, y: number): string => `<path d="M${String(x - 3.6)} ${String(y - 3.6)}l7.2 7.2M${String(x + 3.6)} ${String(y - 3.6)}l-7.2 7.2" ${S(3)}/>`;
const square = (x: number, y: number, b: string): string =>
  `<rect x="${String(x - 5)}" y="${String(y - 5)}" width="10" height="10" rx="2" fill="${W}"/><rect x="${String(x - 1.6)}" y="${String(y - 1.6)}" width="4.2" height="4.2" rx="1" fill="${b}"/>`;
const smile = (y: number, w = 12): string => `<path d="M${String(36 - w / 2)} ${String(y)}q${String(w / 2)} ${String(w * 0.55)} ${String(w)} 0" ${S()}/>`;
const grin = (y: number, w = 15, h = 9): string =>
  `<path d="M${String(36 - w / 2)} ${String(y)}h${String(w)}a${String(w / 2)} ${String(h)} 0 0 1 ${String(-w)} 0z" fill="${W}" stroke="${W}" stroke-width="1.5" stroke-linejoin="round"/>`;
const wavy = (y: number): string => `<path d="M28 ${String(y)}q2 -2.4 4 0t4 0t4 0t4 0" ${S(3)}/>`;
const zigzag = (y: number): string => `<path d="M27 ${String(y)}l3 -3l3 3l3 -3l3 3l3 -3l3 3" ${S(2.6)}/>`;
const fangs = (y: number): string =>
  `<path d="M27 ${String(y)}q9 6 18 0" ${S(3)}/><path d="M30 ${String(y + 2)}l2.2 5.4l2.2 -4.3zM37.6 ${String(y + 3.1)}l2.2 4.3l2.2 -5.4z" fill="${W}" stroke="${W}" stroke-width="1.3" stroke-linejoin="round"/>`;
const scream = (y: number): string => `<ellipse cx="36" cy="${String(y)}" rx="5" ry="7.5" fill="${W}"/>`;
const ohh = (y: number): string => `<ellipse cx="36" cy="${String(y)}" rx="3.2" ry="3.8" fill="${W}"/>`;
const mustache = (y: number): string =>
  `<path d="M36 ${String(y)}c-2.5-3.5-7-3.5-9.5-.5c-1.2 1.4-3 1.8-4.3 .6c1 3.6 5.4 4.8 8.3 3c2.4-1.4 4-2.3 5.5-3.1zM36 ${String(y)}c2.5-3.5 7-3.5 9.5-.5c1.2 1.4 3 1.8 4.3 .6c-1 3.6-5.4 4.8-8.3 3c-2.4-1.4-4-2.3-5.5-3.1z" fill="${W}"/>`;
const brow = (x: number, y: number, tilt: number): string => `<path d="M${String(x - 4.5)} ${String(y + tilt)}L${String(x + 4.5)} ${String(y - tilt)}" ${S(2.8)}/>`;

// Face centre: eyes around y = 38 at x = 28 and 44, the mouth from y = 49.
export const FACES: readonly Face[] = [
  { name: 'Googly', draw: (b: string) => eye(27, 37, b, { rx: 6.6, ry: 7.4, r: 3.4, dx: -2, dy: 2 }) + eye(45, 39, b, { rx: 4, ry: 4.6, r: 2.2, dx: 1.4, dy: -1.6 }) + wavy(52) },
  { name: 'Cyclops', draw: (b: string) => eye(36, 37, b, { rx: 9, ry: 9.6, r: 4.4, dx: 1.5, dy: 1.5 }) + smile(52, 10) },
  { name: 'Three eyes', draw: (b: string) => eye(26, 39, b, { rx: 4.2, ry: 5, r: 2.3 }) + eye(36, 34, b, { rx: 4.2, ry: 5, r: 2.3, dy: -1 }) + eye(46, 39, b, { rx: 4.2, ry: 5, r: 2.3 }) + grin(48, 13, 7) },
  { name: 'Dizzy', draw: () => spiral(28, 38) + spiral(44, 38) + wavy(52) },
  { name: 'Knocked out', draw: () => cross(28, 38) + cross(44, 38) + ohh(52) },
  { name: 'Vampire', draw: (b: string) => brow(28, 31, 1.6) + brow(44, 31, -1.6) + eye(28, 39, b, { rx: 4.4, ry: 4.8, r: 2.4 }) + eye(44, 39, b, { rx: 4.4, ry: 4.8, r: 2.4 }) + fangs(48) },
  { name: 'Mustache', draw: (b: string) => eye(28, 36, b, { rx: 4.6, ry: 5.4, r: 2.5 }) + eye(44, 36, b, { rx: 4.6, ry: 5.4, r: 2.5 }) + mustache(48) },
  { name: 'Scream', draw: (b: string) => eye(28, 35, b, { rx: 4.6, ry: 6.8, r: 2.2, dy: -2 }) + eye(44, 35, b, { rx: 4.6, ry: 6.8, r: 2.2, dy: -2 }) + scream(52) },
  { name: 'Grumpy', draw: (b: string) => brow(28, 31, -2.2) + brow(44, 31, 2.2) + eye(28, 39, b, { rx: 4.6, ry: 4.6, r: 2.6, dy: 0.6 }) + eye(44, 39, b, { rx: 4.6, ry: 4.6, r: 2.6, dy: 0.6 }) + `<path d="M30 53q6 -4 12 0" ${S()}/>` },
  { name: 'Mischief', draw: (b: string) => brow(28, 30, -2) + brow(44, 31, 0) + eye(28, 38, b, { rx: 4.6, ry: 5.2, r: 2.6, dx: 1.4 }) + `<path d="M39.5 38h9" ${S(3)}/>` + `<path d="M29 51q6 3.5 13 -2.5" ${S()}/>` },
  { name: 'Pirate', draw: (b: string) => `<path d="M20 31l30 9" ${S(2.2)}/><ellipse cx="44" cy="38.5" rx="6.6" ry="6" fill="${W}"/>` + eye(28, 38, b, { rx: 5, ry: 5.8, r: 2.8, dx: -1 }) + `<path d="M28 50q8 6 16 -1" ${S()}/>` },
  { name: 'Robot', draw: (b: string) => square(28, 38, b) + square(44, 38, b) + zigzag(52) },
  { name: 'Half asleep', draw: (b: string) => eye(27, 37, b, { rx: 6.4, ry: 7.2, r: 3.2, dx: 1, dy: 1.5 }) + `<path d="M39 39.5a5.5 5 0 0 0 11 0z" fill="${W}"/><path d="M38 39.5h13" ${S(2.6)}/>` + `<ellipse cx="37" cy="52" rx="4.2" ry="5.4" fill="${W}" transform="rotate(-12 37 52)"/>` },
  { name: 'Shocked', draw: (b: string) => eye(28, 37, b, { rx: 6, ry: 7, r: 1.6 }) + eye(44, 37, b, { rx: 6, ry: 7, r: 1.6 }) + ohh(52) },
  { name: 'Spaced out', draw: (b: string) => `<path d="M21.5 36.5a6 6.6 0 0 0 12 0z" fill="${W}"/><circle cx="29.5" cy="38.6" r="2.4" fill="${b}"/><path d="M38.5 37.5a6 6.6 0 0 0 12 0z" fill="${W}"/><circle cx="41" cy="39.6" r="2.4" fill="${b}"/><path d="M20.5 36.5h14M37.5 37.5h14" ${S(2.6)}/>` + `<path d="M29 51q5 4.5 11 1.5q3-1.5 4 1" ${S()}/>` },
  { name: 'Unhinged', draw: (b: string) => eye(26.5, 37, b, { rx: 7, ry: 7.8, r: 1.8, dx: 2, dy: -1 }) + eye(45, 39.5, b, { rx: 3.2, ry: 3.4, r: 1.6, dx: -0.6 }) + `<path d="M26 52l3 -2.6l3 2.6l3 -2.6l3 2.6l3 -2.6l3 2.6" ${S(2.6)}/>` },
  { name: 'Eye roll', draw: (b: string) => eye(28, 38, b, { rx: 5.4, ry: 6.2, r: 2.9, dx: 1.2, dy: -3.4 }) + eye(44, 38, b, { rx: 5.4, ry: 6.2, r: 2.9, dx: 1.2, dy: -3.4 }) + `<path d="M30.5 52h11" ${S()}/>` },
  { name: 'Side-eye', draw: (b: string) => `<path d="M23 31.5l9 1.6M40 33.1l9 -1.6" ${S(2.6)}/>` + eye(28, 39, b, { rx: 5.6, ry: 4.4, r: 2.6, dx: 3, dy: 0 }) + eye(44, 39, b, { rx: 5.6, ry: 4.4, r: 2.6, dx: 3, dy: 0 }) + `<path d="M37 52.5q4 -1.2 7 -3.4" ${S()}/>` },
  { name: 'Evil grin', draw: (b: string) => brow(28, 32, -2.6) + brow(44, 32, 2.6) + eye(28, 39, b, { rx: 4.4, ry: 4.2, r: 1.8, dx: 1, dy: 0.8 }) + eye(44, 39, b, { rx: 4.4, ry: 4.2, r: 1.8, dx: -1, dy: 0.8 }) + `<path d="M23 46q13 16 26 0q-13 6 -26 0z" fill="${W}" stroke="${W}" stroke-width="1.4" stroke-linejoin="round"/>` },
  { name: 'Panic', draw: (b: string) => eye(28, 37, b, { rx: 6, ry: 7.4, r: 1.3 }) + eye(44, 37, b, { rx: 6, ry: 7.4, r: 1.3 }) + `<path d="M25 52.5l2.2 -2.2l2.2 2.2l2.2 -2.2l2.2 2.2l2.2 -2.2l2.2 2.2l2.2 -2.2l2.2 2.2l2.2 -2.2" ${S(2.4)}/>` },
  { name: 'Kissy', draw: () => `<path d="M23.5 38q4.5 -4.6 9 0M39.5 38q4.5 -4.6 9 0" ${S(3)}/>` + `<path d="M34.5 45.5q5 -1 4.6 2.6q-0.3 2 -3.4 2.4q4.4 0.4 4 3q-0.5 3 -5.2 2.4" ${S(3)}/>` },
  { name: 'Upside down', draw: (b: string) => `<path d="M28 38q8 -7 16 0" ${S()}/>` + eye(29, 49, b, { rx: 4.6, ry: 5.4, r: 2.5, dy: -1 }) + eye(43, 49, b, { rx: 4.6, ry: 5.4, r: 2.5, dy: -1 }) },
  { name: 'Enlightened', draw: (b: string) => eye(36, 30, b, { rx: 4.6, ry: 5.8, r: 2.6, dy: 0 }) + `<path d="M23.5 41q4.5 3.6 9 0M39.5 41q4.5 3.6 9 0" ${S(3)}/>` + smile(50, 10) },
  { name: 'Disgusted', draw: (b: string) => `<path d="M23 38.5h10" ${S(3.2)}/>` + brow(44, 31, -1.8) + eye(44, 38.5, b, { rx: 4.4, ry: 5, r: 2.4, dx: -1 }) + `<path d="M27 53q3 -3.4 6 -1.2t6 -1t6 -2.4" ${S(3)}/>` },
  { name: 'Sly', draw: (b: string) => brow(28, 31, 1.4) + `<path d="M39 33.5q5 -3.4 10 -0.6" ${S(2.6)}/>` + eye(28, 38.5, b, { rx: 4.6, ry: 5, r: 2.6, dx: 1.6 }) + `<path d="M39.5 39.5q4.5 2.6 9 0" ${S(3)}/>` + `<path d="M26 47.5q12 10 22 -2.4q-12 4.4 -22 2.4z" fill="${W}" stroke="${W}" stroke-width="1.4" stroke-linejoin="round"/>` },
];

/**
 * Each character's own build: a little wider or narrower, taller or shorter, its crown a touch off-centre and its
 * sides a touch slanted — and a lean. It leans about the middle of its body, so it stays centred in the circle.
 */
/** One build per face, in the same order. */
export const BUILDS: readonly Build[] = [
  { w: 46, top: 16, dx: -1.5, a: 1.5, b: 0.5, c: 3, tilt: -7 },
  { w: 44, top: 15, dx: 1, a: 0, b: 2, c: 5, tilt: 5 },
  { w: 48, top: 17, dx: 0, a: 2, b: 2, c: 2, tilt: -3 },
  { w: 45, top: 16, dx: 2, a: 1, b: 0, c: 4, tilt: 8 },
  { w: 47, top: 18, dx: -2, a: 0.5, b: 1.5, c: 3, tilt: -5 },
  { w: 44, top: 15, dx: 0.5, a: 2, b: 1, c: 6, tilt: 3 },
  { w: 49, top: 17, dx: -1, a: 1, b: 1, c: 2, tilt: -8 },
  { w: 45, top: 16, dx: 1.5, a: 0, b: 1.5, c: 4, tilt: 6 },
  { w: 46, top: 18, dx: -0.5, a: 2, b: 0, c: 3, tilt: -4 },
  { w: 47, top: 16, dx: 2, a: 1, b: 2, c: 5, tilt: 7 },
  { w: 44, top: 17, dx: -2, a: 1.5, b: 1, c: 2, tilt: -6 },
  { w: 48, top: 15, dx: 1, a: 0.5, b: 0.5, c: 4, tilt: 4 },
  { w: 46, top: 16, dx: 0, a: 1, b: 2, c: 3, tilt: -2 },
  { w: 45, top: 17, dx: -1.5, a: 2, b: 1, c: 5, tilt: 6 },
  { w: 47, top: 17, dx: 1, a: 1, b: 1.5, c: 3, tilt: -6 },
  { w: 45, top: 16, dx: -1, a: 0.5, b: 1, c: 4, tilt: 5 },
  { w: 46, top: 24, dx: 0, a: 1, b: 1, c: 3, tilt: -3 },
  { w: 48, top: 16, dx: 1.5, a: 1.5, b: 0.5, c: 5, tilt: 7 },
  { w: 47, top: 18, dx: -1, a: 1, b: 2, c: 2, tilt: -5 },
  { w: 46, top: 16, dx: 1, a: 1, b: 1, c: 4, tilt: 4 },
  { w: 45, top: 17, dx: -0.5, a: 2, b: 0.5, c: 3, tilt: -7 },
  { w: 48, top: 16, dx: 1.5, a: 0.5, b: 1.5, c: 5, tilt: 6 },
  { w: 46, top: 15, dx: -1.5, a: 1.5, b: 1, c: 2, tilt: -4 },
  { w: 47, top: 17, dx: 0.5, a: 1, b: 2, c: 4, tilt: 8 },
  { w: 44, top: 16, dx: -1, a: 0.5, b: 0.5, c: 3, tilt: -6 },
];

const f = (n: number): string => n.toFixed(2);

/**
 * The body: a smooth tombstone that is not quite perfect — the crown a touch off-centre and a little fuller on one
 * side, the sides gently bowed out or in. No bumps: one clean line.
 */
function bodyPath({ w, top, dx, a, b, c }: Build): string {
  const L = 36 - w / 2 + a;
  const R = 36 + w / 2 - b;
  const cx = 36 + dx;
  const y0 = top + w * 0.48;
  // a round crown on both sides (0.55 is a circle's handle), one side a hair fuller than the other
  const kl = 0.58 + (c - 3.5) * 0.008;
  const kr = 0.58 - (c - 3.5) * 0.008;
  const bowL = (c % 2 === 0 ? 0.9 : -0.6) * (a + 0.6);
  const bowR = (c % 2 === 0 ? -0.6 : 0.9) * (b + 0.6);
  return `M${f(36 - w / 2)} 82Q${f(L - bowL)} ${f((82 + y0) / 2)} ${f(L)} ${f(y0)}C${f(L)} ${f(y0 - kl * (y0 - top))} ${f(cx - kl * (cx - L))} ${f(top)} ${f(cx)} ${f(top)}C${f(cx + kr * (R - cx))} ${f(top)} ${f(R)} ${f(y0 - kr * (y0 - top))} ${f(R)} ${f(y0)}Q${f(R + bowR)} ${f((82 + y0) / 2)} ${f(36 + w / 2)} 82Z`;
}

/** A steady number for a seed (FNV-1a), so both devices pick the same face. */
function hash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** The face a seed gets. */
export function faceOf(seed: string): number {
  return hash(seed) % FACES.length;
}

/** One character as an SVG document: its own build, leaning about the middle of its body, centred in the circle. */
export function characterSvg(face: number, tone: Tone): string {
  const i = ((face % FACES.length) + FACES.length) % FACES.length;
  const build = BUILDS[i] ?? BUILDS[0];
  const shape = FACES[i] ?? FACES[0];
  if (build === undefined || shape === undefined) return '';
  const body = BODIES[tone];
  // the circle is drawn here, not clipped by CSS, so its edge is anti-aliased in every browser; the white disc is a hair
  // smaller than the body's clip, so no white fringe shows where the body meets the edge
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72"><clipPath id="c"><circle cx="36" cy="36" r="36"/></clipPath><circle cx="36" cy="36" r="35.4" fill="#fff"/><g clip-path="url(#c)"><g transform="rotate(${String(build.tilt)} 36 46)"><path d="${bodyPath(build)}" fill="${body}"/><g transform="translate(${String(build.dx * 0.5)} ${String(build.top - 16)})">${shape.draw(body)}</g></g></g></svg>`;
}

/** The character as an image address. */
export function characterUri(face: number, tone: Tone): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(characterSvg(face, tone))}`;
}
